import React, { useEffect, useMemo, useRef } from 'react';
import {
  apply12HourParts,
  get12HourParts,
  getClockCoordinates,
  getAllMinuteOptions,
} from './timePickerUtils';

const CLOCK_SIZE = 256;
const CENTER = CLOCK_SIZE / 2;
const NUMBER_RADIUS = 98;
const HAND_LENGTH = 78;

const InlineClockPicker = ({
  value,
  mode,
  onHourSelect,
  onMinuteSelect,
  onBackToHour,
}) => {
  const minuteListRef = useRef(null);
  const { hour12, minutes, isPM } = get12HourParts(value);
  const minuteOptions = useMemo(() => getAllMinuteOptions(), []);

  const hourNumbers = useMemo(
    () => Array.from({ length: 12 }, (_, index) => index + 1),
    []
  );

  const hourToPosition = (hour) => (hour % 12);
  const selectedHourIndex = hourToPosition(hour12);
  const handAngle = 90 - selectedHourIndex * 30;

  useEffect(() => {
    if (mode !== 'minute' || !minuteListRef.current) return;
    const selectedEl = minuteListRef.current.querySelector(
      '.mdt-minute-list__item.is-selected'
    );
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [mode, minutes]);

  const handleHourClick = (hour) => {
    const next = apply12HourParts(value || new Date(), hour, minutes, isPM);
    onHourSelect?.(next);
  };

  const handleMinuteClick = (minute) => {
    const next = apply12HourParts(value || new Date(), hour12, minute, isPM);
    onMinuteSelect?.(next);
  };

  if (mode === 'minute') {
    return (
      <div className="mdt-time-panel mdt-time-panel--minute">
        <div className="mdt-minute-header">
          <button type="button" className="mdt-time-panel__back" onClick={onBackToHour}>
            ‹ Change hour
          </button>
        </div>
        <div className="mdt-minute-list" ref={minuteListRef}>
          {minuteOptions.map((minute) => {
            const isSelected = minute === minutes;
            return (
              <button
                key={minute}
                type="button"
                className={`mdt-minute-list__item${isSelected ? ' is-selected' : ''}`}
                onClick={() => handleMinuteClick(minute)}
              >
                {minute.toString().padStart(2, '0')}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mdt-time-panel mdt-time-panel--hour">
      <div className="mdt-clock">
        <svg
          className="mdt-clock__face"
          viewBox={`0 0 ${CLOCK_SIZE} ${CLOCK_SIZE}`}
          role="presentation"
        >
          <circle className="mdt-clock__dial" cx={CENTER} cy={CENTER} r={NUMBER_RADIUS + 18} />
          {hourNumbers.map((hour) => {
            const { x, y } = getClockCoordinates(
              hourToPosition(hour),
              12,
              NUMBER_RADIUS,
              CENTER,
              CENTER
            );
            const isSelected = hour === hour12;
            return (
              <g key={hour}>
                <circle
                  className={`mdt-clock__number-hit${isSelected ? ' is-selected' : ''}`}
                  cx={x}
                  cy={y}
                  r={18}
                  onClick={() => handleHourClick(hour)}
                />
                <text
                  className={`mdt-clock__number${isSelected ? ' is-selected' : ''}`}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  onClick={() => handleHourClick(hour)}
                >
                  {hour}
                </text>
              </g>
            );
          })}
          <line
            className="mdt-clock__hand"
            x1={CENTER}
            y1={CENTER}
            x2={CENTER + HAND_LENGTH * Math.cos((handAngle * Math.PI) / 180)}
            y2={CENTER - HAND_LENGTH * Math.sin((handAngle * Math.PI) / 180)}
          />
          <circle className="mdt-clock__center" cx={CENTER} cy={CENTER} r={4} />
        </svg>
      </div>
    </div>
  );
};

export default InlineClockPicker;
